import { isWithinValidity } from '../validity.js';
const emptyGroupDef = () => ({
    id: '',
    slug: '',
    name: '',
    description: '',
    color: '',
    active: true,
    order: 0
});
const makeGroupDef = (overrides = {}) => ({
    ...emptyGroupDef(),
    ...overrides
});
const toGroupDef = (raw) => {
    const group = emptyGroupDef();
    if (!raw || typeof raw !== 'object')
        return group;
    group.id = raw.id ?? '';
    group.name = raw.name ?? '';
    // A slug is what phase 2 matches discount patterns on, so derive a usable one
    // rather than leaving it empty for groups created before the field existed.
    group.slug =
        raw.slug || (group.name ? group.name.toLowerCase().replace(/\s+/g, '-') : '');
    group.description = raw.description ?? '';
    group.color = raw.color ?? '';
    group.active = raw.active ?? true;
    group.order = typeof raw.order === 'number' ? raw.order : 0;
    return group;
};
const toGroupDefs = (raw) => {
    if (!Array.isArray(raw))
        return [];
    return raw
        .map(toGroupDef)
        .filter((g) => g.id !== '')
        .sort((a, b) => a.order - b.order);
};
const emptyGroupMembership = () => ({
    groupId: '',
    validFrom: '',
    validTo: '',
    addedBy: '',
    addedAt: '',
    note: ''
});
const toGroupMembership = (raw) => {
    const membership = emptyGroupMembership();
    if (!raw || typeof raw !== 'object')
        return membership;
    membership.groupId = raw.groupId ?? '';
    membership.validFrom = raw.validFrom ?? '';
    membership.validTo = raw.validTo ?? '';
    membership.addedBy = raw.addedBy ?? '';
    membership.addedAt = raw.addedAt ?? '';
    membership.note = raw.note ?? '';
    return membership;
};
const toGroupMemberships = (raw) => {
    if (!Array.isArray(raw))
        return [];
    return raw.map(toGroupMembership).filter((m) => m.groupId !== '');
};
/**
 * Is this membership in effect right now? An expired membership stays on the
 * document — nobody has to delete a row when a membership year rolls over, the
 * engine simply stops seeing it.
 *
 * Read through the shared validity window (ADR 0014): bounds in the site's
 * zone (default Europe/Oslo), Gyldig til including its whole day (D118), and
 * an unparseable bound ignored rather than revoking the membership.
 */
const membershipIsActive = (membership, now, timezone) => isWithinValidity(now, membership, timezone);
/**
 * The group ids a user may act on at `now`. Expiry is applied here and nowhere
 * else, so every caller gets the same answer.
 */
const activeGroupIds = (user, now) => {
    if (!user?.groups)
        return [];
    return user.groups
        .filter((m) => membershipIsActive(m, now))
        .map((m) => m.groupId);
};
/**
 * The active memberships as id/slug/name triples, the shape the discount
 * system's eligibility context wants.
 *
 * Slugs come from `groups` (the site's definitions), never from the membership
 * row — so they cannot be missing or stale. A membership whose group no longer
 * exists is dropped: it can match no `group:` pattern, which is the same
 * fail-closed reading {@link isInAnyGroup} gives it. The name rides along so a
 * group discount can snapshot it onto the ticket and a cart line can say which
 * group earned it.
 */
const activeGroups = (user, groups, now) => {
    if (!user?.groups)
        return [];
    const resolved = [];
    for (const membership of user.groups) {
        if (!membershipIsActive(membership, now))
            continue;
        const def = groupById(groups, membership.groupId);
        if (!def || !def.slug)
            continue;
        resolved.push({ id: membership.groupId, slug: def.slug, name: def.name });
    }
    return resolved;
};
/**
 * Does the user belong to at least one of `restrictedTo`?
 *
 * An empty or absent `restrictedTo` means "not restricted" and is true for
 * everyone. A `restrictedTo` naming only groups that no longer exist is
 * restricted-to-nobody and fails closed — deleting a group must not silently
 * open every slot that referenced it.
 */
const isInAnyGroup = (user, restrictedTo, now) => {
    if (!restrictedTo || restrictedTo.length === 0)
        return true;
    const mine = activeGroupIds(user, now);
    return restrictedTo.some((groupId) => mine.includes(groupId));
};
const groupById = (groups, groupId) => {
    return groups.find((g) => g.id === groupId);
};
/**
 * Human readable label for a set of group ids, used as the default label on a
 * locked slot. Ids with no matching definition are dropped rather than shown
 * raw — see docs/plans/groups.md "What a locked slot says".
 */
const groupNames = (groups, restrictedTo) => {
    if (!restrictedTo || restrictedTo.length === 0)
        return '';
    return restrictedTo
        .map((groupId) => groupById(groups, groupId)?.name ?? '')
        .filter((name) => name !== '')
        .join(', ');
};
const groupUsage = (groupId, site, events = []) => {
    const names = (ids) => ids ?? [];
    const availability = site.availability
        .filter((record) => names(record.restrictedTo).includes(groupId))
        .map((record) => ({
        id: record.id,
        spaceTime: record.spaceTime,
        note: record.note
    }));
    const spaces = site.siteItems
        .filter((space) => names(space.restrictedTo).includes(groupId))
        .map((space) => ({ id: space.id, name: space.name }));
    const eventRefs = [];
    for (const event of events) {
        if (names(event.signup.groups).includes(groupId)) {
            eventRefs.push({ eventId: event.id, eventName: event.name, via: 'signup' });
        }
        if (names(event.discount.groups).includes(groupId)) {
            eventRefs.push({
                eventId: event.id,
                eventName: event.name,
                via: 'discount'
            });
        }
        for (const sub of event.subs) {
            if (names(sub.restrictedTo).includes(groupId)) {
                eventRefs.push({
                    eventId: event.id,
                    eventName: event.name,
                    via: 'sub',
                    subName: sub.name
                });
            }
        }
    }
    return {
        availability,
        spaces,
        events: eventRefs,
        isUnused: availability.length === 0 && spaces.length === 0 && eventRefs.length === 0
    };
};
export { activeGroupIds, activeGroups, emptyGroupDef, emptyGroupMembership, groupById, groupNames, groupUsage, isInAnyGroup, makeGroupDef, membershipIsActive, toGroupDef, toGroupDefs, toGroupMembership, toGroupMemberships };
//# sourceMappingURL=group.js.map